import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import RouteGateFallback from '../ui/RouteGateFallback';
import OrganizationOnboardingGate from './OrganizationOnboardingGate';
import { isLandlordOwner } from '../../utils/portalAuth';
import {
  isTenantSession,
  isPlatformAdminSession,
  resolveTenantPortalNavigation,
} from '../../utils/tenantPortalRouting';
import { authTokenService } from '../../services/auth.service';
import { getAdminAppBaseUrl, mapAdminPathToAdminSpa } from '../../utils/adminAppUrl';

const EMAIL_REGISTERED_AS_TENANT_CODE = 'EMAIL_REGISTERED_AS_TENANT';
const PHONE_VERIFICATION_REQUIRED_CODE = 'PHONE_VERIFICATION_REQUIRED';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [landlordAccessOk, setLandlordAccessOk] = useState<boolean | null>(null);
  const [redirectToTenant, setRedirectToTenant] = useState(false);
  const [redirectToPhoneVerify, setRedirectToPhoneVerify] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || loading) {
      setLandlordAccessOk(null);
      return;
    }

    let isMounted = true;

    const checkAccess = async () => {
      try {
        await apiService.get('/auth/me');
        if (isMounted) setLandlordAccessOk(true);
      } catch (error: any) {
        if (!isMounted) return;
        const status = error.response?.status;
        const code = error.response?.data?.code;
        if (status === 403 && code === EMAIL_REGISTERED_AS_TENANT_CODE) {
          const claims = authTokenService.getClaims();
          const tenantId =
            (error.response?.data?.tenantProfileId as string | undefined) ||
            claims?.tenantProfileId;
          if (tenantId) {
            navigate(
              claims?.mustVerifyPhone
                ? '/tenant/verify-phone'
                : `/tenant/portal/${tenantId}`,
              { replace: true }
            );
            return;
          }
          setRedirectToTenant(true);
          setLandlordAccessOk(false);
          return;
        }
        if (status === 403 && code === PHONE_VERIFICATION_REQUIRED_CODE) {
          setRedirectToPhoneVerify(true);
          setLandlordAccessOk(false);
          return;
        }
        setLandlordAccessOk(false);
      }
    };

    checkAccess();
    return () => { isMounted = false; };
  }, [isAuthenticated, loading]);

  useEffect(() => {
    if (redirectToTenant) navigate('/tenant/login', { replace: true });
  }, [redirectToTenant, navigate]);

  useEffect(() => {
    if (redirectToPhoneVerify) navigate('/verify-phone', { replace: true });
  }, [redirectToPhoneVerify, navigate]);

  if (loading) return <RouteGateFallback variant="auth" />;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Tenants must never enter landlord routes (payments, properties, etc.)
  if (isTenantSession(user)) {
    return <Navigate to={resolveTenantPortalNavigation(user)} replace />;
  }

  // Platform admins must stay on admin plane — no landlord API/UI access
  if (isPlatformAdminSession(user)) {
    const adminBase = getAdminAppBaseUrl();
    if (adminBase) {
      window.location.replace(`${adminBase}${mapAdminPathToAdminSpa('/admin')}`);
      return <RouteGateFallback variant="admin" />;
    }
    return <Navigate to="/admin" replace />;
  }

  if (isLandlordOwner(user || {}) && user?.accountStatus === 'RESTRICTED') {
    return <Navigate to="/signup-pending-approval" replace />;
  }

  if (landlordAccessOk === null) return <RouteGateFallback variant="auth" message="Checking access" />;

  if (landlordAccessOk === false) {
    if (redirectToPhoneVerify) return <RouteGateFallback variant="auth" message="Redirecting to phone verification" />;
    if (redirectToTenant) return <RouteGateFallback variant="auth" message="Redirecting to tenant sign-in" />;
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Org setup is its own page; gate applies to all other landlord routes.
  if (location.pathname === '/org-setup' || location.pathname.startsWith('/org-setup/')) {
    return <>{children}</>;
  }

  return (
    <OrganizationOnboardingGate>
      {children}
    </OrganizationOnboardingGate>
  );
};

export default ProtectedRoute;
